import React, { useMemo, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import {
  getSoldRows, getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice, getTradeLock,
} from '../components/helpers';
import {
  Page, Hero, StatStrip, StatGrid, Section, Empty, Thumb, AllocationBar, Meter,
  getItemTypes, findSkinImage, money, signed, pct, compactMoney, dateShort,
} from './mobileShared';

// The Board — the phone dashboard. Same figures as Overview 3 (they share the
// helpers that define sold/unsold/profit), reordered for a single column:
// net worth first, then the stat rail, then month-by-month, mix, top holds
// and recent activity.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CANDY  = ['#ffd43d', '#ff9bd2', '#6fe8cf', '#b79cff', '#c9f65a', '#ffab5e'];

function MonthTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="mb-tip">
      <span className="l">{label}</span>
      <span className={`v ${v < 0 ? 'down' : 'up'}`}>{signed(v)}</span>
      <span className="s">{payload[0].payload.count} sale{payload[0].payload.count !== 1 ? 's' : ''}</span>
    </div>
  );
}

export default function MobileOverview({
  rows, settings, livePrices, skinsData = [], inventoryMap, zone,
}) {
  const [chartYear, setChartYear] = useState(() => new Date().getFullYear());

  // Charts need literal colours (recharts writes them into SVG attributes),
  // so this page resolves the same values .mb-app puts on --mb-pos/--mb-neg.
  const profC = zone ? '#0d7a43' : (settings?.profitTextColor || '#4ade80');
  const lossC = zone ? '#c8134b' : (settings?.lossTextColor   || '#f87171');
  const ink   = zone ? '#131313' : '#9196aa';
  const grid  = zone ? 'rgba(19,19,19,0.16)' : 'rgba(255,255,255,0.07)';

  const isExcluded = useCallback(r => getItemTypes(r).some(t => {
    const def = settings?.itemTypes?.find(it => it.value === t.value);
    return def?.overrideProfitLoss === true;
  }), [settings?.itemTypes]);

  const availableYears = useMemo(() => {
    const years = new Set();
    rows?.forEach(r => { const y = new Date(r.timestamp).getFullYear(); if (y) years.add(y); });
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  // Mirrors OverviewTabZone/OverviewTab3's `stats` memo exactly.
  const stats = useMemo(() => {
    if (!rows?.length) return null;
    const sold   = getSoldRows(rows);
    const unsold = getUnsoldRows(rows);

    const includedSold  = getIncludedSold(rows, isExcluded);
    const totalProfit   = getTotalProfit(rows, isExcluded);
    const totalSoldCost = includedSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const unsoldCost    = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const itemCount     = rows.filter(r => r.skin?.value).length;

    let unrealizedPnl = 0, marketValue = 0, gainers = 0, losers = 0;
    unsold.forEach(r => {
      const lp  = getLivePrice(livePrices, r);
      const buy = parseFloat(r.buyPrice) || 0;
      if (lp) {
        const market = lp.starting_at?.price || 0;
        marketValue   += market;
        unrealizedPnl += market - buy;
        if (market >= buy) gainers++; else losers++;
      } else {
        marketValue += buy;
      }
    });

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const profit30d = includedSold
      .filter(r => new Date(r.timestamp) >= cutoff)
      .reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);

    let lockedCost = 0, lockedCount = 0, unlock7 = 0;
    const in7 = new Date();
    in7.setDate(in7.getDate() + 7);
    unsold.forEach(r => {
      const inv = r.assetId ? inventoryMap?.get(r.assetId) : null;
      const { locked, until } = getTradeLock(r, inv);
      if (!locked) return;
      lockedCost  += parseFloat(r.buyPrice) || 0;
      lockedCount += 1;
      if (until && until <= in7) unlock7 += 1;
    });

    return {
      totalProfit, unsoldCost, marketValue, unrealizedPnl, itemCount, profit30d,
      soldCount: sold.length, unsoldCount: unsold.length,
      realizedRoi:   totalSoldCost > 0 ? (totalProfit / totalSoldCost) * 100 : null,
      unrealizedRoi: unsoldCost    > 0 ? (unrealizedPnl / unsoldCost) * 100 : null,
      gainers, losers, lockedCost, lockedCount, unlock7,
    };
  }, [rows, livePrices, isExcluded, inventoryMap]);

  const monthlyData = useMemo(() => {
    const months = MONTHS.map((label, i) => ({ label, month: i + 1, profit: 0, count: 0 }));
    rows?.filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      if (d.getFullYear() === chartYear) {
        const m = months[d.getMonth()];
        m.profit += parseFloat(r.profit) || 0;
        m.count  += 1;
      }
    });
    return months.map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows, chartYear, isExcluded]);

  const yearNet = useMemo(() => monthlyData.reduce((s, m) => s + m.profit, 0), [monthlyData]);

  const categoryData = useMemo(() => {
    if (!rows) return [];
    const liveRows = rows.filter(r => r.skin?.value && parseFloat(r.buyPrice) > 0);
    const typed = (settings?.itemTypes || [])
      .filter(t => t.value !== 'Normal')
      .map(type => {
        const typeRows = liveRows.filter(r => getItemTypes(r).some(t => t.value === type.value));
        const value = typeRows.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
        return { name: type.label, value: parseFloat(value.toFixed(2)), color: type.color, count: typeRows.length };
      })
      .filter(d => d.value > 0);

    const uncategorized = liveRows.filter(r => getItemTypes(r).length === 0);
    const uncatValue = uncategorized.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    if (uncatValue > 0) {
      typed.push({ name: 'Uncategorized', value: parseFloat(uncatValue.toFixed(2)), color: '#a8a49a', count: uncategorized.length });
    }
    return typed.map((d, i) => ({ ...d, color: d.color || CANDY[i % CANDY.length] }));
  }, [rows, settings]);

  const topItems = useMemo(() => getUnsoldRows(rows)
    // getUnsoldRows carries the skin-name check and, since the trade fix, drops
    // items that were traded away — they are not holdings any more.
    .filter(r => parseFloat(r.buyPrice) > 0)
    .sort((a, b) => parseFloat(b.buyPrice) - parseFloat(a.buyPrice))
    .slice(0, 5), [rows]);

  const recent = useMemo(() => [...(rows || [])]
    .filter(r => r.skin?.value)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 5), [rows]);

  const perf = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (!sold.length) return null;
    const profits = sold.map(r => parseFloat(r.profit) || 0);
    const wins = profits.filter(p => p > 0).length;
    let best = sold[0], worst = sold[0];
    sold.forEach(r => {
      const p = parseFloat(r.profit) || 0;
      if (p > (parseFloat(best.profit)  || 0)) best  = r;
      if (p < (parseFloat(worst.profit) || 0)) worst = r;
    });
    return {
      count: sold.length, wins, winRate: (wins / sold.length) * 100,
      avg: profits.reduce((s, p) => s + p, 0) / sold.length, best, worst,
    };
  }, [rows, isExcluded]);

  if (!stats) {
    return (
      <Page>
        <Empty>Nothing tracked yet. Add an item from the Armory tab to get started.</Empty>
      </Page>
    );
  }

  const netWorth = stats.marketValue;

  return (
    <Page>
      <Hero
        label="Portfolio value right now"
        value={compactMoney(netWorth)}
        tone={stats.unrealizedPnl >= 0 ? 'up' : 'down'}
        sub={<>
          <span style={{ color: stats.unrealizedPnl >= 0 ? profC : lossC }}>{signed(stats.unrealizedPnl)}</span>
          {stats.unrealizedRoi != null ? ` · ${pct(stats.unrealizedRoi)} if sold today` : ''}
        </>}
        foot={`${stats.unsoldCount} held · ${stats.soldCount} sold · ${stats.itemCount} tracked in total`}
      />

      <StatStrip items={[
        { label: 'Profit banked', value: signed(stats.totalProfit), color: stats.totalProfit >= 0 ? profC : lossC,
          sub: stats.realizedRoi != null ? `${pct(stats.realizedRoi)} realized ROI` : 'no sales yet' },
        { label: '30-day profit', value: signed(stats.profit30d), color: stats.profit30d >= 0 ? profC : lossC,
          sub: 'sales in the last 30 days' },
        { label: 'Total invested', value: money(stats.unsoldCost), sub: 'cost of what you hold' },
        { label: 'Market value', value: money(stats.marketValue), sub: `${stats.gainers} up · ${stats.losers} down` },
        stats.lockedCount > 0 && {
          label: 'Trade locked', value: String(stats.lockedCount),
          sub: `${money(stats.lockedCost)} · ${stats.unlock7} free within 7d`,
        },
      ]} />

      {/* ── Month by month ─────────────────────────── */}
      <Section
        title="Profit by month"
        note={`${chartYear} · net ${signed(yearNet)} across the year`}
        action={availableYears.length > 1 && (
          <select className="mb-mini-select" value={chartYear} onChange={e => setChartYear(Number(e.target.value))} aria-label="Year">
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      >
        <div className="mb-panel chart">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={monthlyData} margin={{ top: 6, right: 4, left: -22, bottom: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: ink, fontSize: 10, fontWeight: 700 }} tickLine={false} axisLine={false} interval={0} />
              <YAxis tick={{ fill: ink, fontSize: 10 }} tickLine={false} axisLine={false} width={46} />
              <ReferenceLine y={0} stroke={grid} />
              <Tooltip cursor={{ fill: 'rgba(129,140,248,0.10)' }} content={<MonthTip />} />
              <Bar dataKey="profit" radius={zone ? 0 : [4, 4, 0, 0]}>
                {monthlyData.map((m, i) => (
                  <Cell key={i} fill={m.profit >= 0 ? profC : lossC} opacity={m.profit === 0 ? 0.18 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* ── Portfolio mix ──────────────────────────── */}
      {categoryData.length > 0 && (
        <Section title="Where the money sits" note="By what you paid, across everything you own">
          <div className="mb-panel">
            <AllocationBar parts={categoryData} />
          </div>
        </Section>
      )}

      {/* ── Top holdings ───────────────────────────── */}
      {topItems.length > 0 && (
        <Section title="Biggest holdings" note="Your five most expensive open positions">
          <div className="mb-list">
            {topItems.map((r, i) => {
              const img = findSkinImage(skinsData, r.skin.value, r.condition?.value);
              const buy = parseFloat(r.buyPrice) || 0;
              const mkt = getLivePrice(livePrices, r)?.starting_at?.price ?? null;
              const diff = mkt != null ? mkt - buy : null;
              return (
                <motion.div key={i} className="mb-row"
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}>
                  <Thumb src={img} />
                  <div className="mb-row-main">
                    <div className="mb-row-name">{r.skin.value}</div>
                    <div className="mb-row-meta">
                      <span>{r.condition?.value || 'n/a'}</span>
                      <span>paid {money(buy)}</span>
                    </div>
                  </div>
                  <div className="mb-row-stub">
                    <span className="v">{mkt != null ? money(mkt) : 'n/a'}</span>
                    {diff != null && (
                      <span className="p" style={{ color: diff >= 0 ? profC : lossC }}>{signed(diff)}</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      )}

      {/* ── Performance ────────────────────────────── */}
      {perf && (
        <Section title="How your trading is going" note={`Across all ${perf.count} completed sale${perf.count !== 1 ? 's' : ''}`}>
          <div className="mb-panel">
            <div className="mb-winrate">
              <div className="mb-winrate-head">
                <span>Win rate</span>
                <b style={{ color: perf.winRate >= 50 ? profC : lossC }}>{perf.winRate.toFixed(0)}%</b>
              </div>
              <Meter ratio={perf.winRate / 100} color={perf.winRate >= 50 ? profC : lossC} />
              <span className="mb-winrate-foot">{perf.wins} of {perf.count} sales made money</span>
            </div>
          </div>
          <StatGrid items={[
            { label: 'Average per sale', value: signed(perf.avg), color: perf.avg >= 0 ? profC : lossC },
            { label: 'Best flip', value: signed(parseFloat(perf.best.profit) || 0), color: profC,
              sub: perf.best.skin?.value },
            { label: 'Worst flip', value: signed(parseFloat(perf.worst.profit) || 0), color: lossC,
              sub: perf.worst.skin?.value },
            { label: 'Realized ROI', value: stats.realizedRoi != null ? pct(stats.realizedRoi) : 'n/a',
              color: (stats.realizedRoi ?? 0) >= 0 ? profC : lossC },
          ]} />
        </Section>
      )}

      {/* ── Recent activity ────────────────────────── */}
      {recent.length > 0 && (
        <Section title="Latest movement" note="The five most recent rows in your tracker">
          <div className="mb-list">
            {recent.map((r, i) => {
              const img  = findSkinImage(skinsData, r.skin.value, r.condition?.value);
              const sold = parseFloat(r.sellPrice) > 0;
              const p    = parseFloat(r.profit) || 0;
              return (
                <div key={i} className="mb-row">
                  <Thumb src={img} size="sm" />
                  <div className="mb-row-main">
                    <div className="mb-row-name">{r.skin.value}</div>
                    <div className="mb-row-meta">
                      <span>{sold ? 'sold' : 'bought'}</span>
                      <span>{dateShort(r.timestamp)}</span>
                    </div>
                  </div>
                  <div className="mb-row-stub">
                    <span className="v" style={sold ? { color: p >= 0 ? profC : lossC } : undefined}>
                      {sold ? signed(p) : money(r.buyPrice)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </Page>
  );
}
